import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '$lib/components/ui/Button';
import { getIntlLocale } from '$lib/i18n';

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ page, pageSize, total, onPageChange }: PaginationProps) {
  const { t } = useTranslation();
  const tag = getIntlLocale();
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  return (
    <div className="flex h-9 items-center justify-between border-t border-rule bg-surface px-3 text-2xs text-fg-muted" data-testid="pagination">
      <span>
        <span className="num text-fg">{start}</span>–<span className="num text-fg">{end}</span> {t('common.of')}{' '}
        <span className="num text-fg">{total.toLocaleString(tag)}</span>
      </span>
      <span className="flex items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          aria-label={t('pagination.previousPage')}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft size={14} />
        </Button>
        <span className="num px-2 text-fg">
          {page} / {pageCount}
        </span>
        <Button
          size="sm"
          variant="ghost"
          aria-label={t('pagination.nextPage')}
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight size={14} />
        </Button>
      </span>
    </div>
  );
}
